/**
 * BookInfographicOverlay — in-page modal opened from the CURRENT BOOK
 * card on /bodha and /sankalpa.
 *
 * Two rendering modes based on data availability for this book/lang:
 *
 *   1. DIAGRAM MODE — if the book has one or more diagrammatic images
 *      registered in DIAGRAMMATIC_INFOGRAPHICS, we render a small
 *      per-language picker at the top of the modal (only showing the
 *      languages that actually have an uploaded image for THIS book).
 *      Selecting a language swaps the shown image. The picker is
 *      LOCAL to the overlay — it does not change the site-wide
 *      language.
 *
 *   2. TEXT-CARD FALLBACK — if no image exists for the currently
 *      selected diagram-language (or the book has zero diagram images
 *      registered), we render the existing text-card infographic in
 *      that language. Never a broken/empty state.
 */
import React, { useEffect, useRef, useState, useMemo } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import BookInfographicBody from "./BookInfographicBody";
import {
  getAvailableDiagramLanguages,
  getDiagrammaticImage,
  LANG_LABELS,
} from "../data/diagrammaticInfographics";
import { T } from "./homev3/tokens";

// Local-storage key so the user's diagram-language choice sticks across
// re-opens for the same book. Per-book so different books can start on
// their own natural default.
const LS_KEY = (bookId) => `icraftz:diagram-lang:${bookId}`;

function DiagramLanguagePicker({ available, selected, onSelect }) {
  return (
    <div
      role="tablist"
      aria-label="Diagram language"
      data-testid="diagram-lang-picker"
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 6,
        margin: "0 0 14px",
        paddingBottom: 12,
        borderBottom: `1px solid ${T.GOLD}44`,
      }}
    >
      <span style={{
        fontFamily: T.F_HEADING, fontSize: 10, letterSpacing: "0.14em",
        color: T.GOLD, alignSelf: "center", marginRight: 6,
      }}>
        LANGUAGE
      </span>
      {available.map((lang) => {
        const active = lang === selected;
        return (
          <button
            key={lang}
            role="tab"
            aria-selected={active}
            data-testid={`diagram-lang-${lang}`}
            onClick={() => onSelect(lang)}
            style={{
              padding: "4px 12px",
              fontFamily: T.F_BODY,
              fontSize: 13,
              lineHeight: 1.4,
              border: `1px solid ${active ? T.DARK_BROWN : T.GOLD}88`,
              background: active ? T.DARK_BROWN : "transparent",
              color: active ? T.CREAM || "#FBF2DF" : T.DARK_BROWN,
              borderRadius: 999,
              cursor: "pointer",
              transition: "background 140ms ease, color 140ms ease",
            }}
            onMouseEnter={(e) => {
              if (!active) e.currentTarget.style.background = `${T.GOLD}22`;
            }}
            onMouseLeave={(e) => {
              if (!active) e.currentTarget.style.background = "transparent";
            }}
          >
            {LANG_LABELS[lang] || lang}
          </button>
        );
      })}
    </div>
  );
}

function DiagramImagePane({ src, alt }) {
  return (
    <figure
      style={{ margin: 0, textAlign: "center" }}
      data-testid="diagram-image-pane"
    >
      <img
        src={src}
        alt={alt}
        style={{
          display: "block",
          maxWidth: "100%",
          height: "auto",
          margin: "0 auto",
          borderRadius: 6,
          boxShadow: "0 2px 14px rgba(74,42,21,0.10)",
        }}
      />
    </figure>
  );
}

export default function BookInfographicOverlay({
  open,
  onOpenChange,
  bookId,
  bookSnapshot = null,
  lang = "en",
}) {
  const contentRef = useRef(null);

  // Languages this book has diagram images for.
  const diagramLangs = useMemo(
    () => getAvailableDiagramLanguages(bookId),
    [bookId]
  );

  // Selected diagram-language. Priority: last-used (localStorage) →
  // site lang if it has an image → first available diagram lang.
  const [selectedLang, setSelectedLang] = useState(() => {
    if (typeof window === "undefined") return diagramLangs[0] || lang;
    const stored = window.localStorage.getItem(LS_KEY(bookId));
    if (stored && diagramLangs.includes(stored)) return stored;
    if (diagramLangs.includes(lang)) return lang;
    return diagramLangs[0] || lang;
  });

  // Persist selection so re-opening the same book restores the last choice.
  useEffect(() => {
    if (typeof window === "undefined" || !selectedLang) return;
    window.localStorage.setItem(LS_KEY(bookId), selectedLang);
  }, [bookId, selectedLang]);

  // When bookId changes, re-seed the selection.
  useEffect(() => {
    if (!diagramLangs.length) return;
    setSelectedLang((prev) =>
      diagramLangs.includes(prev) ? prev : diagramLangs[0]
    );
  }, [bookId, diagramLangs]);

  // Reset scroll on open / book change.
  useEffect(() => {
    if (open && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [open, bookId, selectedLang]);

  // Resolve what to render.
  const imageSrc = diagramLangs.length
    ? getDiagrammaticImage(bookId, selectedLang)
    : null;
  const showDiagram = Boolean(imageSrc);
  // Fallback: no image for selectedLang → text-card in THAT language.
  const fallbackLang = selectedLang || lang;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="bic-overlay-scrim"
          data-testid="bic-overlay-scrim"
        />
        <DialogPrimitive.Content
          ref={contentRef}
          className="bic-overlay-content"
          data-testid="bic-overlay"
          onEscapeKeyDown={() => onOpenChange(false)}
          aria-label="Book infographic"
        >
          <DialogPrimitive.Title className="sr-only">
            Book infographic
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            A parchment distilling this book&apos;s core premise, insights,
            model, action items, quote and one-line takeaway.
          </DialogPrimitive.Description>

          <DialogPrimitive.Close
            className="bic-overlay-close"
            data-testid="bic-overlay-close"
            aria-label="Close infographic"
          >
            <X size={18} strokeWidth={1.5} />
          </DialogPrimitive.Close>

          <div className="bic-overlay-body">
            {open && diagramLangs.length > 0 && (
              <DiagramLanguagePicker
                available={diagramLangs}
                selected={selectedLang}
                onSelect={setSelectedLang}
              />
            )}
            {open && showDiagram && (
              <DiagramImagePane
                src={imageSrc}
                alt={`Infographic for book ${bookId} in ${selectedLang}`}
              />
            )}
            {open && !showDiagram && (
              <BookInfographicBody
                bookId={bookId}
                bookSnapshot={bookSnapshot}
                lang={fallbackLang}
                backHref={null}
              />
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
